<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";

const blankForm = () => ({
  title: "",
  message: "",
  link: "",
  active: true,
  type: "info",
  priority: 0,
  targetUserId: "",
});

export default {
  name: "NotificationsMgt",
  data() {
    return {
      notifications: [],
      recipients: [],
      selected: null,
      form: blankForm(),
      editingId: null,
      showForm: false,
      searchQuery: "",
      searchTimer: null,
      filters: { active: "", type: "", audience: "", source: "" },
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      loading: false,
      saving: false,
      initialized: false,
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    canManage() {
      return this.hasPermission("notifications", "manage");
    },
    pageRange() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.load(1), 350);
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.load(1);
    },
    "filters.active"() {
      if (this.initialized) this.load(1);
    },
    "filters.type"() {
      if (this.initialized) this.load(1);
    },
    "filters.audience"() {
      if (this.initialized) this.load(1);
    },
    "filters.source"() {
      if (this.initialized) this.load(1);
    },
  },
  async mounted() {
    this.initialized = true;
    await Promise.all([this.load(1), this.loadRecipients()]);
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    async load(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get("/notifications", {
          params: {
            page,
            limit: this.pagination.itemsPerPage,
            search: this.searchQuery.trim() || undefined,
            active: this.filters.active || undefined,
            type: this.filters.type || undefined,
            audience: this.filters.audience || undefined,
            source: this.filters.source || undefined,
          },
        });
        this.notifications = response.data.data.notifications || [];
        const pagination = response.data.data.pagination || {};
        this.pagination.currentPage = pagination.page || page;
        this.pagination.totalPages = pagination.pages || 1;
        this.pagination.totalItems = pagination.total || 0;
      } catch (error) {
        this.showError(error, "Failed to fetch notifications");
      } finally {
        this.loading = false;
      }
    },
    async loadRecipients(search = "") {
      try {
        const response = await axios.get("/notifications/recipients/options", {
          params: { search: search || undefined },
        });
        this.recipients = response.data.data || [];
      } catch (error) {
        this.showError(error, "Failed to load recipients");
      }
    },
    recipientName(user) {
      return (
        [user?.lastName, user?.firstName, user?.otherName]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    audience(item) {
      if (!item.targetUserId) return "All portal users";
      return `${this.recipientName(item.targetUserId)}${
        item.applicationId?.applicationNumber
          ? ` · ${item.applicationId.applicationNumber}`
          : ""
      }`;
    },
    typeClass(type) {
      return (
        {
          info: "text-bg-info",
          warning: "text-bg-warning",
          success: "text-bg-success",
          danger: "text-bg-danger",
        }[type] || "text-bg-secondary"
      );
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleString("en-GB");
    },
    clearFilters() {
      this.initialized = false;
      this.searchQuery = "";
      this.filters = { active: "", type: "", audience: "", source: "" };
      this.$nextTick(() => {
        this.initialized = true;
        this.load(1);
      });
    },
    openCreate() {
      this.editingId = null;
      this.form = blankForm();
      this.showForm = true;
    },
    edit(item) {
      if (item.isSystem) return;
      this.editingId = item._id;
      this.form = {
        title: item.title,
        message: item.message,
        link: item.link || "",
        active: item.active,
        type: item.type,
        priority: item.priority || 0,
        targetUserId: item.targetUserId?._id || "",
      };
      this.showForm = true;
    },
    async save() {
      this.saving = true;
      try {
        const recipient = this.recipients.find(
          (item) => item._id === this.form.targetUserId
        );
        const payload = {
          ...this.form,
          targetUserId: this.form.targetUserId || null,
          applicationId: recipient?.application?._id || null,
        };
        if (this.editingId)
          await axios.patch(`/notifications/${this.editingId}`, payload);
        else await axios.post("/notifications", payload);
        this.showForm = false;
        await this.load(this.pagination.currentPage);
        await Swal.fire("Saved", "Notification saved successfully", "success");
      } catch (error) {
        this.showError(error, "Could not save notification");
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      const result = await Swal.fire({
        title: "Delete notification?",
        text: "This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/notifications/${item._id}`);
        await this.load(this.pagination.currentPage);
      } catch (error) {
        this.showError(error, "Could not delete notification");
      }
    },
    async toggle(item) {
      try {
        await axios.patch(`/notifications/${item._id}`, {
          active: !item.active,
        });
        await this.load(this.pagination.currentPage);
      } catch (error) {
        this.showError(error, "Could not update notification status");
      }
    },
    showError(error, fallback) {
      Swal.fire("Error", error.response?.data?.message || fallback, "error");
    },
  },
};
</script>

<template>
  <div class="container py-4 text-start">
    <div
      class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Notification Management</h4>
        <p class="text-muted mb-0">
          Create and manage global or recipient-specific portal notices.
        </p>
      </div>
      <button
        v-if="canManage"
        class="btn btn-primary btn-sm"
        @click="openCreate"
      >
        <i class="bi bi-plus-circle me-1"></i>Add Notification
      </button>
    </div>
    <div class="card border-0 shadow-sm">
      <div class="card-header bg-white">
        <div class="row g-3">
          <div class="col-md-7">
            <div class="input-group">
              <span class="input-group-text bg-white"
                ><i class="bi bi-search"></i></span
              ><input
                v-model="searchQuery"
                class="form-control border-start-0"
                placeholder="Search notification title, message, or link"
              />
            </div>
          </div>
          <div class="col-md-5 d-flex justify-content-md-end">
            <div class="dropdown">
              <button
                class="btn btn-sm btn-outline-secondary dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                <i class="bi bi-funnel me-1"></i>Filters
              </button>
              <div
                class="dropdown-menu dropdown-menu-end p-3 filter-menu"
                @click.stop
              >
                <div class="d-flex justify-content-between mb-3">
                  <strong>Filter Notifications</strong
                  ><button
                    class="btn btn-link btn-sm p-0"
                    @click="clearFilters"
                  >
                    Clear all
                  </button>
                </div>
                <label class="small text-muted">Status</label
                ><select
                  v-model="filters.active"
                  class="form-select form-select-sm mb-3"
                >
                  <option value="">All statuses</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option></select
                ><label class="small text-muted">Type</label
                ><select
                  v-model="filters.type"
                  class="form-select form-select-sm mb-3"
                >
                  <option value="">All types</option>
                  <option
                    v-for="type in ['info', 'warning', 'success', 'danger']"
                    :key="type"
                    :value="type"
                  >
                    {{ type }}
                  </option></select
                ><label class="small text-muted">Audience</label
                ><select
                  v-model="filters.audience"
                  class="form-select form-select-sm mb-3"
                >
                  <option value="">All audiences</option>
                  <option value="global">Global</option>
                  <option value="targeted">Individual</option></select
                ><label class="small text-muted">Source</label
                ><select
                  v-model="filters.source"
                  class="form-select form-select-sm"
                >
                  <option value="">All sources</option>
                  <option value="system">System</option>
                  <option value="staff">Staff-created</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">Title</th>
              <th>Message</th>
              <th>Timestamp</th>
              <th>Type</th>
              <th>Target</th>
              <th>Status</th>
              <th class="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-5">
                <div class="spinner-border"></div>
              </td>
            </tr>
            <tr v-else-if="!notifications.length">
              <td colspan="7" class="text-center py-5 text-muted">
                No notifications matched the current filters.
              </td>
            </tr>
            <tr v-for="item in notifications" v-else :key="item._id">
              <td class="ps-4">
                <strong>{{ item.title }}</strong
                ><small v-if="item.isSystem" class="badge text-bg-dark ms-2"
                  >System</small
                >
              </td>
              <td class="message-cell">{{ item.message }}</td>
              <td>{{ formatDate(item.createdAt) }}</td>
              <td>
                <span class="badge" :class="typeClass(item.type)">{{
                  item.type
                }}</span>
              </td>
              <td>{{ audience(item) }}</td>
              <td>
                <span
                  class="badge"
                  :class="item.active ? 'text-bg-success' : 'text-bg-secondary'"
                  >{{ item.active ? "Active" : "Inactive" }}</span
                >
              </td>
              <td class="text-end pe-4">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-outline-dark"
                    @click="selected = item"
                  >
                    <i class="bi bi-eye"></i></button
                  ><template v-if="canManage && !item.isSystem"
                    ><button
                      class="btn btn-sm btn-outline-primary"
                      @click="edit(item)"
                    >
                      <i class="bi bi-pencil"></i></button
                    ><button
                      class="btn btn-sm btn-outline-secondary"
                      @click="toggle(item)"
                    >
                      <i
                        :class="item.active ? 'bi bi-pause' : 'bi bi-play'"
                      ></i></button
                    ><button
                      class="btn btn-sm btn-outline-danger"
                      @click="remove(item)"
                    >
                      <i class="bi bi-trash"></i></button
                  ></template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-3"
      >
        <div>
          <small>Per page</small
          ><select
            v-model.number="pagination.itemsPerPage"
            class="form-select form-select-sm d-inline-block ms-2 per-page"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
        <ul class="pagination pagination-sm mb-0">
          <li
            v-for="page in pageRange"
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.currentPage }"
          >
            <button class="page-link" @click="load(page)">{{ page }}</button>
          </li>
        </ul>
        <small
          >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
          {{ paginationInfo.total }}</small
        >
      </div>
    </div>

    <div v-if="showForm" class="modal-bg" @click.self="showForm = false">
      <form class="modal-card bg-white rounded shadow" @submit.prevent="save">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <h5>{{ editingId ? "Edit Notification" : "Add Notification" }}</h5>
          <button
            type="button"
            class="btn-close"
            @click="showForm = false"
          ></button>
        </div>
        <div class="p-4">
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Title</label
              ><input v-model.trim="form.title" class="form-control" required />
            </div>
            <div class="col-md-4">
              <label class="form-label">Type</label
              ><select v-model="form.type" class="form-select">
                <option
                  v-for="type in ['info', 'warning', 'success', 'danger']"
                  :key="type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label">Message</label
              ><textarea
                v-model.trim="form.message"
                class="form-control"
                rows="4"
                required
              ></textarea>
            </div>
            <div class="col-md-8">
              <label class="form-label">Audience</label
              ><select v-model="form.targetUserId" class="form-select">
                <option value="">All portal users</option>
                <option
                  v-for="user in recipients"
                  :key="user._id"
                  :value="user._id"
                >
                  {{ recipientName(user) }} —
                  {{ user.application?.applicationNumber || user.role }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Priority</label
              ><input
                v-model.number="form.priority"
                type="number"
                class="form-control"
              />
            </div>
            <div class="col-md-9">
              <label class="form-label">Optional Link</label
              ><input
                v-model.trim="form.link"
                type="url"
                class="form-control"
              />
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <label class="form-check"
                ><input
                  v-model="form.active"
                  type="checkbox"
                  class="form-check-input me-2"
                />Active</label
              >
            </div>
          </div>
        </div>
        <div class="p-3 border-top text-end">
          <button
            type="button"
            class="btn btn-light me-2"
            @click="showForm = false"
          >
            Cancel</button
          ><button class="btn btn-primary" :disabled="saving">
            {{ saving ? "Saving…" : "Save Notification" }}
          </button>
        </div>
      </form>
    </div>
    <div v-if="selected" class="modal-bg" @click.self="selected = null">
      <div class="modal-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0">{{ selected.title }}</h5>
            <small>{{ formatDate(selected.createdAt) }}</small>
          </div>
          <button class="btn-close" @click="selected = null"></button>
        </div>
        <div class="p-4">
          <p class="lead">{{ selected.message }}</p>
          <div class="row g-3">
            <div class="col-md-6">
              <strong>Audience:</strong> {{ audience(selected) }}
            </div>
            <div class="col-md-6">
              <strong>Type:</strong> {{ selected.type }}
            </div>
            <div class="col-md-6">
              <strong>Priority:</strong> {{ selected.priority }}
            </div>
            <div class="col-md-6">
              <strong>Source:</strong>
              {{ selected.isSystem ? "System" : "Staff-created" }}
            </div>
            <div v-if="selected.link" class="col-12">
              <strong>Link:</strong>
              <a :href="selected.link" target="_blank" rel="noopener">{{
                selected.link
              }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-menu {
  min-width: 320px;
}
.per-page {
  width: 80px;
}
.message-cell {
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal-bg {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  width: min(800px, 100%);
  max-height: 92vh;
  overflow: auto;
}
</style>
